import clsx from "clsx";
import { useEffect, useState } from "react";
import { VisuallyHidden } from "@/components";
import type { DecimalString } from "@/lib/decimal";
import { MISSING_VALUE, formatGrams } from "@/lib/format";
import { useBalance } from "./balance/useBalance";
import styles from "./BalanceReadout.module.css";

export interface BalanceReadoutProps {
  /** What to show; the live balance reading by default (pass a line's added mass while weighing). */
  value?: DecimalString | null;
  label?: string;
  size?: "hero" | "compact";
  decimals?: number;
  /** Show the stability and connection line under the number. */
  showState?: boolean;
  className?: string;
}

function stateText(status: string, stable: boolean, hasReading: boolean, noData: boolean): string {
  if (status === "reconnecting") return "Disconnected: plug the balance back in";
  if (status === "connecting") return "Connecting…";
  if (status !== "connected") return "No balance connected";
  if (!hasReading) return noData ? "No data from the balance" : "Waiting for a reading…";
  return stable ? "Stable" : "Settling…";
}

/**
 * The live balance reading in large tabular figures, with a stable indicator. Stability changes are
 * announced politely; individual readings are not.
 */
export function BalanceReadout({
  value,
  label = "Balance",
  size = "hero",
  decimals = 3,
  showState = true,
  className,
}: BalanceReadoutProps) {
  const balance = useBalance();
  const shown = value === undefined ? (balance.reading?.grams ?? null) : value;
  const text = formatGrams(shown, { decimals });
  const state = stateText(balance.status, balance.stable, balance.reading !== null, balance.noData !== null);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setAnnouncement(state), 250);
    return () => window.clearTimeout(timer);
  }, [state]);

  return (
    <div
      className={clsx(styles.readout, className)}
      data-size={size}
      data-stable={balance.status === "connected" && balance.stable ? "true" : "false"}
      data-status={balance.status}
      data-no-data={balance.noData ?? undefined}
    >
      <span className={styles.label}>{label}</span>
      <span className={styles.value} data-missing={text === MISSING_VALUE || undefined}>
        {text}
        <span className={styles.unit}>g</span>
      </span>
      {showState ? (
        <span className={styles.state}>
          <span className={styles.dot} aria-hidden="true" />
          {state}
        </span>
      ) : null}
      <VisuallyHidden>
        <span aria-live="polite">{announcement}</span>
      </VisuallyHidden>
    </div>
  );
}
